import { type RefObject, useEffect } from "react";

import { useReactFlow } from "@xyflow/react";

import { RULE_GRAPH_NODE_HEIGHT, RULE_GRAPH_NODE_WIDTH } from "./layout";
import { isNodeOutsideViewport } from "./run-walk";

/**
 * Follow-when-off-screen camera for the scenario run walk. Renders nothing;
 * on each beat it recenters the viewport on the active node ONLY when that
 * node isn't already visible — the walk mostly plays out under a viewport
 * that never moves, and a fully fit canvas never triggers it at all.
 *
 * Programmatic moves don't trip the canvas's own onMoveStart (xyflow passes
 * a null event), so the camera reports through `onViewportMoved` — the
 * viewport genuinely moved, and "Back to start" should appear.
 *
 * Mounting with no active node does nothing, which is what keeps the
 * hidden-canvas fit behavior untouched; a zero-size canvas (hidden tab,
 * jsdom) is skipped outright.
 */
export const RuleGraphRunCamera = ({
  activeNodeId,
  layoutRevision,
  canvasRef,
  onViewportMoved,
}: {
  activeNodeId: string | null;
  layoutRevision?: string;
  canvasRef: RefObject<HTMLDivElement | null>;
  onViewportMoved: () => void;
}) => {
  const { getNode, getViewport, setCenter } = useReactFlow();

  useEffect(() => {
    if (activeNodeId === null) return;
    const canvas = canvasRef.current;
    if (canvas === null) return;
    const canvasSize = {
      width: canvas.clientWidth,
      height: canvas.clientHeight,
    };
    if (canvasSize.width === 0 || canvasSize.height === 0) return;
    const node = getNode(activeNodeId);
    if (node === undefined) return;
    const width = node.measured?.width ?? node.width ?? RULE_GRAPH_NODE_WIDTH;
    const height =
      node.measured?.height ?? node.height ?? RULE_GRAPH_NODE_HEIGHT;
    const viewport = getViewport();
    const outside = isNodeOutsideViewport({
      nodeRect: { x: node.position.x, y: node.position.y, width, height },
      viewport,
      canvasSize,
    });
    if (!outside) return;
    onViewportMoved();
    void setCenter(node.position.x + width / 2, node.position.y + height / 2, {
      duration: 300,
      zoom: viewport.zoom,
    });
  }, [activeNodeId, layoutRevision]);

  return null;
};
